import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { ethers } from 'ethers';
import MicrojobPaymentABI from 'abis/MicrojobPayment';
import {
  listMicrojobCycles,
  getMicrojobCycle,
  createMicrojobCycle,
  updateMicrojobCycle,
  deleteMicrojobCycle,
  importMicrojobCsv,
  importMicrojobSessionLog,
  submitMicrojobCycle,
  approveMicrojobCycle,
  rejectMicrojobCycle,
  getMicrojobExecutePayload,
  markMicrojobExecuted,
} from 'helpers/microjob.api';
import { buttonStyles } from '../css/commonStyles';
import {
  alertErrorSx,
  colors,
  dateFormFieldStyles,
  fieldBlockStyles,
  fieldErrorTextSx,
  fieldLabelStyles,
  formFieldStyles,
  formGridSx,
  modalInnerTableCardSx,
  modalInnerTableContainerSx,
  newRequestBtnSx,
  pageTitleSx,
  previewSummaryCardSx,
  previewSummaryGridSx,
  previewSummaryLabelSx,
  previewSummaryValueSx,
  requiredSpanSx,
  tabHeaderRowSx,
  tableAlertErrorSx,
  tableCardStyles,
  tableContainerStyles,
  filterToolbarSx,
  makeSelectSx,
  selectMenuProps,
  disabledFieldSx,
  managementBoxSx,
  saveButtonSx,
} from '../css/revenueItems.styles';
import {
  memberCancelBtnSx,
  memberConfirmBtnSx,
  memberDangerBtnSx,
  memberDialogBtnRowSx,
  memberDialogContentSx,
  memberDialogPaperSx,
  memberDialogTitleSx,
  memberDialogTitleWrapSx,
  memberFieldLabelSx,
  memberFieldsStackSx,
  memberHeaderActionBtnSx,
} from '../css/daoMemberModal.styles';
import MemberRowActions from './MemberRowActions';
import { useMicrojobRoles, isValidMicrojobAddress } from '../utils/microjobRoles.utils';
import { connectMetaMaskWallet } from '../utils/metamaskWallet.utils';
import { ensureCorrectChain } from '../utils/vaultChain.utils';
import { formatRevenueSharingErrorMessage, isTxUserRejection } from '../utils/revenueSharing.utils';
import { revenueSharingMessage } from '../utils/revenueSharingMessage';
import { buildPaginationItems, formatDateOnly, REVENUE_ITEMS_PAGE_SIZE } from '../utils/revenueItems.utils';
import { updateDaoMicrojobConfig } from 'helpers/paymentRequest.api';
import MicrojobOperatorTeamSection from './MicrojobOperatorTeamSection';
import VaultNotConfiguredBanner from './VaultNotConfiguredBanner';
import PaymentsPagination from './PaymentsPagination';

const SUB_TAB = {
  SETTINGS: 'settings',
  CYCLES: 'cycles',
};

const CSV_TEMPLATE = 'wallet_address,job_type,job_count\n';

const CYCLE_STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending_review', label: 'Pending review' },
  { value: 'approved', label: 'Approved' },
  { value: 'executed', label: 'Executed' },
  { value: 'rejected', label: 'Rejected' },
];

const ACTIVE_MICROJOB_CYCLE_STATUSES = ['draft', 'pending_review', 'approved'];

const MICROJOB_CONTRACT_CHANGE_BLOCKED_MSG =
  'Cannot change MicrojobPayment address while draft, pending review, or approved cycles exist. Delete or complete those cycles first.';

const RATE_STATUS_META = {
  pending: { label: 'Pending', color: '#eab308', bgColor: 'rgba(234, 179, 8, 0.15)' },
  synced: { label: 'Synced', color: '#059669', bgColor: 'rgba(5, 150, 105, 0.15)' },
  removed: { label: 'Removed', color: '#9aa4b2', bgColor: 'rgba(154, 164, 178, 0.15)' },
};

const CYCLE_STATUS_META = {
  draft: { label: 'Draft', color: '#9aa4b2', bgColor: 'rgba(154, 164, 178, 0.15)' },
  pending_review: { label: 'Pending review', color: '#eab308', bgColor: 'rgba(234, 179, 8, 0.15)' },
  approved: { label: 'Approved', color: '#0284c7', bgColor: 'rgba(2, 132, 199, 0.15)' },
  executed: { label: 'Executed', color: '#059669', bgColor: 'rgba(5, 150, 105, 0.15)' },
  rejected: { label: 'Rejected', color: '#dc2626', bgColor: 'rgba(220, 38, 38, 0.15)' },
};

const walletCellSx = {
  fontFamily: 'monospace',
  fontSize: '0.85rem',
  wordBreak: 'break-all',
};

const MicrojobStatusBadge = ({ status, metaMap }) => {
  const key = String(status || '').toLowerCase();
  const meta = metaMap[key] || {
    label: status || '—',
    color: '#9aa4b2',
  };
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '2px',
          bgcolor: meta.color,
          flexShrink: 0,
        }}
      />
      <Typography
        component="span"
        sx={{
          fontSize: '0.875rem',
          color: meta.color,
          fontFamily: 'Questrial, sans-serif',
          lineHeight: 1.4,
          whiteSpace: 'nowrap',
        }}
      >
        {meta.label}
      </Typography>
    </Box>
  );
};

const toUsdtUnits = (value) => {
  const raw = String(value ?? '0').trim();
  if (!raw) return ethers.BigNumber.from(0);
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return ethers.BigNumber.from(0);
  // Cap to USDT decimals so float artifacts (e.g. 0.30000000000000004) don't
  // throw "fractional component exceeds decimals" in parseUnits.
  return ethers.utils.parseUnits(n.toFixed(6), 6);
};

const ensureBytes32 = (value) => {
  const raw = String(value || '').trim();
  if (/^0x[0-9a-fA-F]{64}$/.test(raw)) return raw;
  if (/^[0-9a-fA-F]{64}$/.test(raw)) return `0x${raw}`;
  try {
    return ethers.utils.hexZeroPad(ethers.BigNumber.from(raw || 0).toHexString(), 32);
  } catch {
    return ethers.constants.HashZero;
  }
};

const parseMicrojobCsvText = (text) => {
  const lines = String(text || '')
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) return { rows: [], error: 'CSV file is empty.' };

  const headerCells = lines[0].split(',').map((c) => c.trim().toLowerCase().replace(/^"|"$/g, ''));
  const hasHeader =
    headerCells.includes('wallet_address') ||
    headerCells.includes('job_type') ||
    headerCells.includes('job_count');

  const startIdx = hasHeader ? 1 : 0;
  const colIndex = (name, fallback) => {
    const idx = headerCells.indexOf(name);
    return idx >= 0 ? idx : fallback;
  };
  const walletIdx = hasHeader ? colIndex('wallet_address', 0) : 0;
  const jobTypeIdx = hasHeader ? colIndex('job_type', 1) : 1;
  const jobCountIdx = hasHeader ? colIndex('job_count', 2) : 2;

  const rows = [];
  for (let i = startIdx; i < lines.length; i += 1) {
    const cells = lines[i].split(',').map((c) => c.trim().replace(/^"|"$/g, ''));
    rows.push({
      wallet_address: cells[walletIdx] || '',
      job_type: cells[jobTypeIdx] || '',
      job_count: cells[jobCountIdx] || '',
    });
  }

  if (!rows.length) return { rows: [], error: 'CSV has no data rows.' };
  return { rows, error: null };
};

const downloadCsvTemplate = () => {
  const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'microjob-import-template.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const ENTRY_STATUS_META = {
  valid: { label: 'Valid', color: '#059669', bgColor: 'rgba(5, 150, 105, 0.15)' },
  invalid: { label: 'Invalid', color: '#dc2626', bgColor: 'rgba(220, 38, 38, 0.15)' },
};

const cycleModalPaperSx = {
  ...memberDialogPaperSx,
  minWidth: { xs: 'calc(100% - 32px)', md: 1040 },
  maxWidth: 1200,
};

const cycleSectionLabelSx = {
  color: colors.textSecondary,
  fontSize: '0.75rem',
  fontFamily: 'Questrial, sans-serif',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  mb: 1.5,
};

const cycleInfoBoxSx = {
  borderRadius: '10px',
  border: '1px solid rgba(26, 238, 194, 0.35)',
  backgroundColor: 'rgba(26, 238, 194, 0.08)',
  color: colors.textSecondary,
  px: 2,
  py: 1.5,
  mb: 2,
  fontFamily: 'Questrial, sans-serif',
  fontSize: '0.9rem',
  lineHeight: 1.5,
};

const cycleWarningBoxSx = {
  ...cycleInfoBoxSx,
  border: '1px solid rgba(234, 179, 8, 0.45)',
  backgroundColor: 'rgba(234, 179, 8, 0.1)',
  color: '#fbbf24',
  mb: 2,
};

const cycleRejectBoxSx = {
  ...cycleInfoBoxSx,
  border: '1px solid rgba(220, 38, 38, 0.45)',
  backgroundColor: 'rgba(220, 38, 38, 0.1)',
  color: '#f87171',
  mb: 2,
};

const cycleSecondaryBtnSx = {
  minHeight: 40,
  px: 2,
  borderRadius: '10px',
  textTransform: 'none',
  fontFamily: 'Questrial, sans-serif',
  fontSize: '0.95rem',
  fontWeight: 500,
  boxShadow: 'none',
  color: colors.textPrimary,
  border: '1px solid rgba(255,255,255,0.22)',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.32)',
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    color: 'rgba(243,244,247,0.38)',
    borderColor: 'rgba(255,255,255,0.12)',
  },
};

const cycleImportBtnSx = {
  ...cycleSecondaryBtnSx,
  color: colors.accent,
  border: `1px solid ${colors.accent}`,
  '&:hover': {
    backgroundColor: 'rgba(26, 238, 194, 0.08)',
    border: `1px solid ${colors.accent}`,
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    color: 'rgba(26, 238, 194, 0.35)',
    borderColor: 'rgba(26, 238, 194, 0.22)',
  },
};

const toDateInputValue = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const toUtcDayStartFromInput = (yyyyMmDd) => {
  if (!yyyyMmDd || !/^\d{4}-\d{2}-\d{2}$/.test(yyyyMmDd)) return null;
  const [y, m, d] = yyyyMmDd.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
};

const toUtcDayStartFromValue = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

/** Client-side period overlap check against loaded cycles list (API remains source of truth). */
const findLocalPeriodOverlap = (cycleList, periodStart, periodEnd, excludeCycleId = null) => {
  const start = toUtcDayStartFromInput(periodStart);
  const end = toUtcDayStartFromInput(periodEnd);
  if (start == null || end == null || end < start) return null;

  return (
    (cycleList || []).find((cycle) => {
      if (excludeCycleId && String(cycle.cycleId) === String(excludeCycleId)) return false;
      const otherStart = toUtcDayStartFromValue(cycle.periodStart);
      const otherEnd = toUtcDayStartFromValue(cycle.periodEnd);
      if (otherStart == null || otherEnd == null) return false;
      return start <= otherEnd && otherStart <= end;
    }) || null
  );
};

const formatRatesSnapshot = (snapshot) => {
  if (!snapshot || typeof snapshot !== 'object') return '—';
  const parts = Object.entries(snapshot).map(([jobType, meta]) => {
    const rate = meta?.rate != null ? meta.rate : meta;
    return `${jobType}=$${rate}`;
  });
  return parts.length ? parts.join(', ') : '—';
};

const MicrojobSection = ({ currentDao, onRefreshDao }) => {
  const daoId = currentDao?._id;
  const microjobRoles = useMicrojobRoles(
    currentDao?.microjobPaymentAddress,
    currentDao?.chainId,
    currentDao?.organizerAddress,
  );
  const {
    walletAddress,
    isOrganizer,
    isMicrojobAdmin,
    isMicrojobOperator,
    hasMicrojobConfigured,
    operatorRows,
    loading: rolesLoading,
    refresh: refreshRoles,
  } = microjobRoles;

  const [subTab, setSubTab] = useState(SUB_TAB.CYCLES);

  const [microjobPaymentAddress, setMicrojobPaymentAddress] = useState(
    currentDao?.microjobPaymentAddress ?? '',
  );
  const [isUpdatingMicrojob, setIsUpdatingMicrojob] = useState(false);

  const [rates, setRates] = useState([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesError, setRatesError] = useState('');

  const [cycles, setCycles] = useState([]);
  const [cyclesLoading, setCyclesLoading] = useState(false);
  const [cyclesError, setCyclesError] = useState('');
  const [cycleStatusFilter, setCycleStatusFilter] = useState('');
  const [cyclePage, setCyclePage] = useState(1);
  const filteredCycles = useMemo(
    () => (cycleStatusFilter ? cycles.filter((c) => c.status === cycleStatusFilter) : cycles),
    [cycles, cycleStatusFilter],
  );
  const cyclePagination = useMemo(() => {
    const totalItems = filteredCycles.length;
    const limit = REVENUE_ITEMS_PAGE_SIZE;
    const totalPages = Math.max(1, Math.ceil(totalItems / limit));
    const page = Math.min(Math.max(cyclePage, 1), totalPages);
    return { page, limit, totalItems, totalPages };
  }, [filteredCycles.length, cyclePage]);
  const pagedCycles = useMemo(() => {
    const start = (cyclePagination.page - 1) * cyclePagination.limit;
    return filteredCycles.slice(start, start + cyclePagination.limit);
  }, [filteredCycles, cyclePagination.page, cyclePagination.limit]);
  const cyclePaginationItems = useMemo(
    () => buildPaginationItems(cyclePagination.page, cyclePagination.totalPages),
    [cyclePagination.page, cyclePagination.totalPages],
  );
  const cyclePaginationLabel = useMemo(() => {
    if (!cyclePagination.totalItems) {
      return cyclesLoading ? 'Loading microjob cycles...' : 'No microjob cycles yet';
    }
    const start = (cyclePagination.page - 1) * cyclePagination.limit + 1;
    const end = Math.min(start + pagedCycles.length - 1, cyclePagination.totalItems);
    return `Showing ${start}-${end} of ${cyclePagination.totalItems} cycles`;
  }, [cyclePagination, pagedCycles.length, cyclesLoading]);

  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [rateForm, setRateForm] = useState({ jobType: '', rate: '' });
  const [rateFormErrors, setRateFormErrors] = useState({});
  const [editingExistingRate, setEditingExistingRate] = useState(false);
  const [rateBusy, setRateBusy] = useState('');

  const [cycleDialogOpen, setCycleDialogOpen] = useState(false);
  const [cycleForm, setCycleForm] = useState({ periodStart: '', periodEnd: '' });
  const [cycleDraftId, setCycleDraftId] = useState(null);
  const [cyclePreview, setCyclePreview] = useState(null);
  const [cycleImportSummary, setCycleImportSummary] = useState(null);
  const [cycleBusy, setCycleBusy] = useState('');

  const [selectedCycleId, setSelectedCycleId] = useState(null);
  const [cycleDetail, setCycleDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [actionBusy, setActionBusy] = useState('');

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState('');

  useEffect(() => {
    setMicrojobPaymentAddress(currentDao?.microjobPaymentAddress ?? '');
  }, [currentDao?.microjobPaymentAddress]);

  useEffect(() => {
    if (subTab === SUB_TAB.SETTINGS) {
      setMicrojobPaymentAddress(currentDao?.microjobPaymentAddress ?? '');
    }
  }, [subTab, currentDao?.microjobPaymentAddress]);

  const showTxError = (err, fallback) => {
    const msg = formatRevenueSharingErrorMessage(err, fallback);
    if (isTxUserRejection(err)) revenueSharingMessage.warning('Transaction was rejected.');
    else revenueSharingMessage.error(msg);
  };

  const ensureWallet = useCallback(async () => {
    let address = walletAddress;
    if (!address) {
      address = await connectMetaMaskWallet();
      await refreshRoles();
    }
    return address;
  }, [walletAddress, refreshRoles]);

  const getSignedContract = async () => {
    await ensureCorrectChain(currentDao?.chainId);
    const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
    const signer = provider.getSigner();
    return new ethers.Contract(
      String(currentDao.microjobPaymentAddress).trim(),
      MicrojobPaymentABI,
      signer,
    );
  };

  const getReadContract = async () => {
    await ensureCorrectChain(currentDao?.chainId);
    const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
    return new ethers.Contract(
      String(currentDao.microjobPaymentAddress).trim(),
      MicrojobPaymentABI,
      provider,
    );
  };

  const loadRates = useCallback(async () => {
    if (!daoId || !hasMicrojobConfigured) return;
    if (!window.ethereum) {
      setRates([]);
      setRatesError('Connect a wallet to load on-chain job type rates.');
      return;
    }
    setRatesLoading(true);
    setRatesError('');
    try {
      const contract = await getReadContract();
      const result = await contract.getAllRates();
      const jobTypes = result?.jobTypes || result?.[0] || [];
      const rateValues = result?.rates || result?.[1] || [];
      const rows = [];
      for (let i = 0; i < jobTypes.length; i += 1) {
        const jobType = String(jobTypes[i] || '').trim();
        if (!jobType) continue;
        rows.push({
          jobType,
          rate: ethers.utils.formatUnits(rateValues[i] || 0, 6),
          onChainStatus: 'synced',
        });
      }
      rows.sort((a, b) => a.jobType.localeCompare(b.jobType));
      setRates(rows);
    } catch (err) {
      setRates([]);
      setRatesError(formatRevenueSharingErrorMessage(err, 'Failed to load microjob rates.'));
    } finally {
      setRatesLoading(false);
    }
  }, [daoId, hasMicrojobConfigured, currentDao?.microjobPaymentAddress, currentDao?.chainId]);

  const loadCycles = useCallback(async () => {
    if (!daoId || !hasMicrojobConfigured) return;
    setCyclesLoading(true);
    setCyclesError('');
    try {
      const res = await listMicrojobCycles(daoId, {}, walletAddress || undefined);
      setCycles(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setCycles([]);
      setCyclesError(formatRevenueSharingErrorMessage(err, 'Failed to load microjob cycles.'));
    } finally {
      setCyclesLoading(false);
    }
  }, [daoId, hasMicrojobConfigured, walletAddress]);

  const loadCycleDetail = useCallback(
    async (cycleId) => {
      if (!daoId || !cycleId) return;
      setDetailLoading(true);
      setDetailError('');
      try {
        const res = await getMicrojobCycle(daoId, cycleId, walletAddress || undefined);
        setCycleDetail(res?.data || null);
      } catch (err) {
        setCycleDetail(null);
        setDetailError(formatRevenueSharingErrorMessage(err, 'Failed to load cycle detail.'));
      } finally {
        setDetailLoading(false);
      }
    },
    [daoId, walletAddress],
  );

  useEffect(() => {
    if (subTab === SUB_TAB.SETTINGS) loadRates();
  }, [subTab, loadRates]);

  useEffect(() => {
    if (subTab === SUB_TAB.CYCLES) loadCycles();
  }, [subTab, loadCycles]);

  useEffect(() => {
    setCyclePage(1);
  }, [cycleStatusFilter, daoId]);

  useEffect(() => {
    if (cyclePage > cyclePagination.totalPages) {
      setCyclePage(cyclePagination.totalPages);
    }
  }, [cyclePage, cyclePagination.totalPages]);

  useEffect(() => {
    if (selectedCycleId) loadCycleDetail(selectedCycleId);
  }, [selectedCycleId, loadCycleDetail]);

  const openRateDialog = (existing) => {
    setEditingExistingRate(!!existing?.jobType);
    setRateForm({
      jobType: existing?.jobType || '',
      rate: existing?.rate || '',
    });
    setRateFormErrors({});
    setRateDialogOpen(true);
  };

  const handleSaveRate = async () => {
    const jobType = String(rateForm.jobType || '').trim();
    const rate = String(rateForm.rate || '').trim();
    const nextErrors = {};
    if (!jobType) nextErrors.jobType = 'Job type is required.';
    if (!rate) nextErrors.rate = 'Rate is required.';
    else if (!(Number(rate) > 0)) nextErrors.rate = 'Rate must be a number greater than 0.';
    if (Object.keys(nextErrors).length) {
      setRateFormErrors(nextErrors);
      return;
    }
    setRateFormErrors({});
    setRateBusy('save');
    try {
      await ensureWallet();
      const contract = await getSignedContract();
      const adminRole = await contract.ADMIN_ROLE();
      const signerAddress = await contract.signer.getAddress();
      const hasAdmin = await contract.hasRole(adminRole, signerAddress);
      if (!hasAdmin) {
        revenueSharingMessage.error(
          'Connected wallet must have ADMIN role on the MicrojobPayment contract.',
        );
        return;
      }
      const onChainRate = ethers.utils.parseUnits(rate, 6);
      const tx = await contract.setUnitPaymentRate(jobType, onChainRate);
      await tx.wait();
      revenueSharingMessage.success('Rate set on-chain.');
      setRateDialogOpen(false);
      await loadRates();
    } catch (err) {
      showTxError(err, 'Failed to set rate on-chain.');
    } finally {
      setRateBusy('');
    }
  };

  const handleRemoveRate = async (rateRow) => {
    if (!window.confirm(`Remove job type "${rateRow.jobType}"?`)) return;
    setRateBusy(`remove:${rateRow.jobType}`);
    try {
      await ensureWallet();
      const contract = await getSignedContract();
      const adminRole = await contract.ADMIN_ROLE();
      const signerAddress = await contract.signer.getAddress();
      const hasAdmin = await contract.hasRole(adminRole, signerAddress);
      if (!hasAdmin) {
        revenueSharingMessage.error(
          'Connected wallet must have ADMIN role on the MicrojobPayment contract.',
        );
        return;
      }
      const tx = await contract.removeUnitPaymentRate(rateRow.jobType);
      await tx.wait();
      revenueSharingMessage.success('Rate removed on-chain.');
      await loadRates();
    } catch (err) {
      showTxError(err, 'Failed to remove rate.');
    } finally {
      setRateBusy('');
    }
  };

  const resetCycleEditor = () => {
    setCycleForm({ periodStart: '', periodEnd: '' });
    setCycleDraftId(null);
    setCyclePreview(null);
    setCycleImportSummary(null);
    setCycleBusy('');
  };

  const closeCycleEditor = () => {
    if (cycleBusy) return;
    setCycleDialogOpen(false);
    resetCycleEditor();
  };

  const openNewCycleDialog = () => {
    resetCycleEditor();
    setCycleDialogOpen(true);
  };

  const applyCyclePreview = (data) => {
    setCyclePreview(data || null);
    const meta = data?.csvImportMeta;
    if (meta && (meta.validCount > 0 || meta.invalidCount > 0)) {
      setCycleImportSummary({
        validCount: meta.validCount || 0,
        invalidCount: meta.invalidCount || 0,
        invalid: meta.invalidRows || [],
      });
    }
  };

  const openCycleEditor = async (cycle) => {
    resetCycleEditor();
    setCycleForm({
      periodStart: toDateInputValue(cycle.periodStart),
      periodEnd: toDateInputValue(cycle.periodEnd),
    });
    setCycleDraftId(cycle.cycleId);
    setCycleDialogOpen(true);
    setCycleBusy('load');
    try {
      const address = await ensureWallet();
      const res = await getMicrojobCycle(daoId, cycle.cycleId, address);
      applyCyclePreview(res?.data || null);
    } catch (err) {
      revenueSharingMessage.error(
        formatRevenueSharingErrorMessage(err, 'Failed to load cycle.'),
      );
    } finally {
      setCycleBusy('');
    }
  };

  const openCycleFromTable = (cycle) => {
    if (cycle.status === 'draft' || cycle.status === 'rejected') {
      openCycleEditor(cycle);
      return;
    }
    setSelectedCycleId(cycle.cycleId);
  };

  const handleDeleteCycle = async (cycle) => {
    if (!cycle?.cycleId) return;
    if (!['draft', 'rejected'].includes(cycle.status)) {
      revenueSharingMessage.error('Only draft or rejected cycles can be deleted.');
      return;
    }
    if (
      !window.confirm(
        `Delete cycle ${cycle.cycleId}? Job logs and payout preview for this cycle will be removed.`,
      )
    ) {
      return;
    }

    setActionBusy(`delete:${cycle.cycleId}`);
    try {
      const address = await ensureWallet();
      await deleteMicrojobCycle(daoId, cycle.cycleId, address);
      revenueSharingMessage.success('Cycle deleted.');
      if (cycleDraftId === cycle.cycleId) {
        setCycleDialogOpen(false);
        resetCycleEditor();
      }
      if (selectedCycleId === cycle.cycleId) {
        setSelectedCycleId(null);
        setCycleDetail(null);
        setDetailError('');
      }
      await loadCycles();
    } catch (err) {
      revenueSharingMessage.error(
        formatRevenueSharingErrorMessage(err, 'Failed to delete cycle.'),
      );
    } finally {
      setActionBusy('');
    }
  };

  const ensureDraftCycle = async (address) => {
    if (!cycleForm.periodStart || !cycleForm.periodEnd) {
      throw new Error('Period start and end are required.');
    }
    if (cycleForm.periodEnd < cycleForm.periodStart) {
      throw new Error('Period end must be on or after period start.');
    }

    const body = {
      periodStart: cycleForm.periodStart,
      periodEnd: cycleForm.periodEnd,
    };

    if (cycleDraftId) {
      return cycleDraftId;
    }

    const res = await createMicrojobCycle(daoId, body, address);
    const createdId = res?.data?.cycleId;
    if (!createdId) throw new Error('Failed to create cycle.');
    setCycleDraftId(createdId);
    await loadCycles();
    return createdId;
  };

  const syncCyclePeriod = async (cycleId, address) => {
    await updateMicrojobCycle(
      daoId,
      cycleId,
      {
        periodStart: cycleForm.periodStart,
        periodEnd: cycleForm.periodEnd,
      },
      address,
    );
  };

  const handleCycleImportCsv = async (file) => {
    if (!file) return;
    if (!cycleForm.periodStart || !cycleForm.periodEnd) {
      revenueSharingMessage.error('Period start and end are required.');
      return;
    }
    if (cycleForm.periodEnd < cycleForm.periodStart) {
      revenueSharingMessage.error('Period end must be on or after period start.');
      return;
    }
    if (findLocalPeriodOverlap(cycles, cycleForm.periodStart, cycleForm.periodEnd, cycleDraftId)) {
      revenueSharingMessage.error('Period overlaps an existing cycle.');
      return;
    }

    setCycleBusy('import');
    try {
      const text = await file.text();
      const parsed = parseMicrojobCsvText(text);
      if (parsed.error) {
        revenueSharingMessage.error(parsed.error);
        return;
      }
      const address = await ensureWallet();
      const cycleId = await ensureDraftCycle(address);
      const res = await importMicrojobCsv(daoId, cycleId, parsed.rows, address);
      const data = res?.data || {};
      try {
        await syncCyclePeriod(cycleId, address);
      } catch {
        // Period sync is best-effort after import (cycle is draft).
      }
      setCycleImportSummary({
        validCount: data.valid?.length ?? data.cycle?.csvImportMeta?.validCount ?? 0,
        invalidCount: data.invalid?.length ?? data.cycle?.csvImportMeta?.invalidCount ?? 0,
        invalid: data.invalid || data.cycle?.csvImportMeta?.invalidRows || [],
      });
      const detail = await getMicrojobCycle(daoId, cycleId, address);
      applyCyclePreview(detail?.data || { ...data.cycle, entries: data.valid, payouts: [] });
      revenueSharingMessage.success('CSV imported.');
      await loadCycles();
    } catch (err) {
      revenueSharingMessage.error(
        formatRevenueSharingErrorMessage(err, err?.message || 'Failed to import CSV.'),
      );
    } finally {
      setCycleBusy('');
    }
  };

  const isSessionLogDao = ['CED', 'TECD'].includes((currentDao?.code || '').toUpperCase());

  const handleImportSessionLog = async () => {
    if (!cycleForm.periodStart || !cycleForm.periodEnd) {
      revenueSharingMessage.error('Period start and end are required.');
      return;
    }
    if (cycleForm.periodEnd < cycleForm.periodStart) {
      revenueSharingMessage.error('Period end must be on or after period start.');
      return;
    }
    if (findLocalPeriodOverlap(cycles, cycleForm.periodStart, cycleForm.periodEnd, cycleDraftId)) {
      revenueSharingMessage.error('Period overlaps an existing cycle.');
      return;
    }

    setCycleBusy('import-session');
    try {
      const address = await ensureWallet();
      const logRes = await importMicrojobSessionLog(daoId, cycleForm.periodStart, cycleForm.periodEnd, address);
      const rows = logRes?.data?.rows;
      if (!rows || !rows.length) {
        revenueSharingMessage.error('No session log entries found for this period.');
        return;
      }
      const cycleId = await ensureDraftCycle(address);
      const res = await importMicrojobCsv(daoId, cycleId, rows, address, { skipMemberCheck: true });
      const data = res?.data || {};
      try {
        await syncCyclePeriod(cycleId, address);
      } catch {}
      setCycleImportSummary({
        validCount: data.valid?.length ?? data.cycle?.csvImportMeta?.validCount ?? 0,
        invalidCount: data.invalid?.length ?? data.cycle?.csvImportMeta?.invalidCount ?? 0,
        invalid: data.invalid || data.cycle?.csvImportMeta?.invalidRows || [],
      });
      const detail = await getMicrojobCycle(daoId, cycleId, address);
      applyCyclePreview(detail?.data || { ...data.cycle, entries: data.valid, payouts: [] });
      revenueSharingMessage.success('Session log imported successfully.');
      await loadCycles();
    } catch (err) {
      revenueSharingMessage.error(
        formatRevenueSharingErrorMessage(err, err?.message || 'Failed to import session log.'),
      );
    } finally {
      setCycleBusy('');
    }
  };

  const handleCycleSubmitForReview = async () => {
    const validEntries = (cyclePreview?.entries || []).filter(
      (e) => e.validationStatus === 'valid',
    );
    if (!cycleDraftId || !validEntries.length) {
      revenueSharingMessage.error('Import a valid CSV before submitting for review.');
      return;
    }
    setCycleBusy('submit');
    try {
      const address = await ensureWallet();
      try {
        await syncCyclePeriod(cycleDraftId, address);
      } catch {
        // Keep submit flow even if period patch fails (e.g. already pending).
      }
      await submitMicrojobCycle(daoId, cycleDraftId, address);
      revenueSharingMessage.success('Cycle submitted for review.');
      setCycleDialogOpen(false);
      resetCycleEditor();
      await loadCycles();
    } catch (err) {
      revenueSharingMessage.error(
        formatRevenueSharingErrorMessage(err, 'Failed to submit cycle.'),
      );
    } finally {
      setCycleBusy('');
    }
  };

  const handleApprove = async () => {
    setActionBusy('approve');
    try {
      const address = await ensureWallet();
      await approveMicrojobCycle(daoId, selectedCycleId, address);
      revenueSharingMessage.success('Cycle approved.');
      await loadCycleDetail(selectedCycleId);
      await loadCycles();
    } catch (err) {
      revenueSharingMessage.error(formatRevenueSharingErrorMessage(err, 'Failed to approve cycle.'));
    } finally {
      setActionBusy('');
    }
  };

  const handleReject = async () => {
    setActionBusy('reject');
    try {
      const address = await ensureWallet();
      await rejectMicrojobCycle(daoId, selectedCycleId, rejectNote || null, address);
      revenueSharingMessage.success('Cycle rejected.');
      setRejectOpen(false);
      setRejectNote('');
      await loadCycleDetail(selectedCycleId);
      await loadCycles();
    } catch (err) {
      revenueSharingMessage.error(formatRevenueSharingErrorMessage(err, 'Failed to reject cycle.'));
    } finally {
      setActionBusy('');
    }
  };

  const executeCycleOnChain = async ({ cycleId, busyKey = 'execute', reloadDetail = false }) => {
    if (!cycleId) return;
    setActionBusy(busyKey);
    try {
      const address = await ensureWallet();
      const res = await getMicrojobExecutePayload(daoId, cycleId, address);
      const payload = res?.data || {};
      const tokenAmounts = (payload.tokenAmounts || []).map((a) => toUsdtUnits(a));
      const allocHash = ensureBytes32(payload.allocHash);
      const contract = await getSignedContract();
      const tx = await contract.submitCalculation(
        payload.cycleId,
        payload.recipients,
        payload.jobTypes,
        payload.jobCounts,
        tokenAmounts,
        allocHash,
      );
      await tx.wait();
      await markMicrojobExecuted(daoId, cycleId, tx.hash, address);
      revenueSharingMessage.success('Cycle executed on-chain.');
      if (reloadDetail) {
        await loadCycleDetail(cycleId);
      }
      await loadCycles();
    } catch (err) {
      showTxError(err, 'Failed to execute cycle on-chain.');
    } finally {
      setActionBusy('');
    }
  };

  const handleExecute = async () => {
    await executeCycleOnChain({ cycleId: selectedCycleId, busyKey: 'execute', reloadDetail: true });
  };

  const entries = cycleDetail?.entries || [];
  const payouts = cycleDetail?.payouts || [];
  const cycleStatus = cycleDetail?.status;

  const cycleEditorEntries = cyclePreview?.entries || [];
  const cycleEditorPayouts = cyclePreview?.payouts || [];
  const cycleEditorValidCount = cycleEditorEntries.filter(
    (e) => e.validationStatus === 'valid',
  ).length;

  const periodOverlapCycle = useMemo(
    () =>
      findLocalPeriodOverlap(
        cycles,
        cycleForm.periodStart,
        cycleForm.periodEnd,
        cycleDraftId,
      ),
    [cycles, cycleForm.periodStart, cycleForm.periodEnd, cycleDraftId],
  );
  const periodOverlapError = periodOverlapCycle
    ? 'Period overlaps an existing cycle.'
    : '';
  const periodRangeInvalid =
    Boolean(cycleForm.periodStart) &&
    Boolean(cycleForm.periodEnd) &&
    cycleForm.periodEnd < cycleForm.periodStart;
  const canImportCycleCsv =
    isMicrojobOperator &&
    Boolean(cycleForm.periodStart) &&
    Boolean(cycleForm.periodEnd) &&
    !periodRangeInvalid &&
    !periodOverlapError;
  const hasInvalidEntries = cycleEditorEntries.some((e) => e.validationStatus === 'invalid');
  const canSubmitCycleEditor =
    isMicrojobOperator &&
    cycleEditorValidCount > 0 &&
    !hasInvalidEntries &&
    Boolean(cycleDraftId) &&
    !periodRangeInvalid &&
    !periodOverlapError;

  const canReview = isOrganizer && cycleStatus === 'pending_review';
  const canExecute = isMicrojobOperator && cycleStatus === 'approved';

  const visibleRates = useMemo(() => rates || [], [rates]);
  const cycleBusyAny = Boolean(cycleBusy);

  const handleSaveMicrojobAddress = async () => {
    if (!isOrganizer) {
      revenueSharingMessage.error(
        'Connect the DAO organizer wallet to update the MicrojobPayment address.',
      );
      return;
    }
    const addr = microjobPaymentAddress.trim();
    if (addr && !isValidMicrojobAddress(addr)) {
      revenueSharingMessage.error('Invalid MicrojobPayment address (0x...).');
      return;
    }
    const currentAddr = String(currentDao?.microjobPaymentAddress || '').trim().toLowerCase();
    const nextAddr = addr ? addr.toLowerCase() : '';
    const isAddressChanging = currentAddr !== nextAddr;

    try {
      setIsUpdatingMicrojob(true);
      const wallet = await ensureWallet();

      if (isAddressChanging) {
        const res = await listMicrojobCycles(daoId, {}, wallet);
        const cycleList = Array.isArray(res?.data) ? res.data : [];
        const hasActiveCycles = cycleList.some((cycle) =>
          ACTIVE_MICROJOB_CYCLE_STATUSES.includes(cycle.status),
        );
        if (hasActiveCycles) {
          revenueSharingMessage.error(MICROJOB_CONTRACT_CHANGE_BLOCKED_MSG);
          return;
        }
      }

      await updateDaoMicrojobConfig(
        daoId,
        { microjobPaymentAddress: addr || null },
        wallet,
      );
      revenueSharingMessage.success('MicrojobPayment address updated.');
      if (onRefreshDao) onRefreshDao();
      await refreshRoles();
    } catch (err) {
      revenueSharingMessage.error(
        formatRevenueSharingErrorMessage(err, 'Failed to update MicrojobPayment address.'),
      );
    } finally {
      setIsUpdatingMicrojob(false);
    }
  };

  return (
    <Box>
      <Box sx={tabHeaderRowSx}>
        <Typography variant="h6" sx={pageTitleSx}>
          Microjob
        </Typography>
        {!walletAddress && !rolesLoading && (
          <Button
            variant="contained"
            onClick={async () => {
              try {
                await connectMetaMaskWallet();
                await refreshRoles();
              } catch (err) {
                revenueSharingMessage.error(
                  formatRevenueSharingErrorMessage(err, 'Failed to connect wallet.'),
                );
              }
            }}
            sx={{ ...buttonStyles, ...newRequestBtnSx }}
          >
            Connect wallet
          </Button>
        )}
      </Box>

      <Typography
        sx={{
          color: colors.textSecondary,
          fontFamily: 'Questrial, sans-serif',
          mb: 2,
          maxWidth: 800,
        }}
      >
        Manage MicrojobPayment settings, unit rates, and payout cycles for this DAO.
      </Typography>

      <Tabs
        value={subTab}
        onChange={(_, v) => setSubTab(v)}
        sx={{
          mb: 2,
          minHeight: 0,
          borderBottom: `1px solid ${colors.border}`,
          '& .MuiTab-root': {
            color: colors.textSecondary,
            textTransform: 'none',
            fontFamily: 'Questrial, sans-serif',
            minHeight: 40,
          },
          '& .Mui-selected': { color: colors.accent },
          '& .MuiTabs-indicator': { backgroundColor: colors.accent },
        }}
      >
        <Tab label="Cycles" value={SUB_TAB.CYCLES} />
        <Tab label="Settings" value={SUB_TAB.SETTINGS} />
      </Tabs>

      {subTab === SUB_TAB.SETTINGS && (
        <>
          <Box sx={{ ...managementBoxSx, maxWidth: 720, mb: 3 }}>
            <Box sx={fieldBlockStyles}>
              <Typography sx={fieldLabelStyles}>MicrojobPayment Address</Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'stretch',
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                }}
              >
                <TextField
                  placeholder="0x..."
                  value={microjobPaymentAddress}
                  onChange={(e) => setMicrojobPaymentAddress(e.target.value)}
                  fullWidth
                  disabled={!isOrganizer}
                  sx={{ ...disabledFieldSx, flex: 1, minWidth: 240 }}
                />
                {isOrganizer && (
                  <Button
                    variant="contained"
                    onClick={handleSaveMicrojobAddress}
                    disabled={isUpdatingMicrojob}
                    sx={{
                      ...buttonStyles,
                      ...saveButtonSx,
                      mt: 0,
                      minWidth: 96,
                      height: 'auto',
                      flexShrink: 0,
                    }}
                  >
                    {isUpdatingMicrojob ? (
                      <CircularProgress size={20} sx={{ color: '#fff' }} />
                    ) : (
                      'Save'
                    )}
                  </Button>
                )}
              </Box>
            </Box>
          </Box>

          {hasMicrojobConfigured ? (
            <MicrojobOperatorTeamSection
              microjobPaymentAddress={currentDao?.microjobPaymentAddress}
              chainId={currentDao?.chainId}
              operatorRows={operatorRows}
              isMicrojobAdmin={isMicrojobAdmin}
              onUpdated={async () => {
                await refreshRoles();
                if (onRefreshDao) onRefreshDao();
              }}
              rolesLoading={rolesLoading}
            />
          ) : (
            <VaultNotConfiguredBanner message="Save a MicrojobPayment address above to manage the Operator team and job type rates." />
          )}

          {hasMicrojobConfigured && (
            <Box sx={{ mt: 4 }}>
              <Box sx={tabHeaderRowSx}>
                <Typography
                  sx={{
                    color: colors.textPrimary,
                    fontFamily: 'Questrial, sans-serif',
                    fontSize: '1.125rem',
                    fontWeight: 400,
                  }}
                >
                  Job type rates (USDT)
                </Typography>
                {isMicrojobAdmin && (
                  <Button
                    variant="contained"
                    onClick={() => openRateDialog(null)}
                    sx={{ ...buttonStyles, ...memberHeaderActionBtnSx }}
                  >
                    + Add job type
                  </Button>
                )}
              </Box>

              {ratesError && (
                <Alert severity="error" sx={tableAlertErrorSx}>
                  {ratesError}
                </Alert>
              )}

              <Box sx={{ ...tableCardStyles, mt: 0 }}>
                <TableContainer component={Box} sx={tableContainerStyles}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Job type</TableCell>
                        <TableCell>Rate (USDT)</TableCell>
                        <TableCell>On-chain status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ratesLoading && (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                            <CircularProgress size={22} sx={{ color: colors.accent }} />
                          </TableCell>
                        </TableRow>
                      )}
                      {!ratesLoading &&
                        visibleRates.map((row) => (
                          <TableRow key={row.jobType}>
                            <TableCell>{row.jobType}</TableCell>
                            <TableCell>{row.rate}</TableCell>
                            <TableCell>
                              <MicrojobStatusBadge status={row.onChainStatus} metaMap={RATE_STATUS_META} />
                            </TableCell>
                            <TableCell align="right">
                              {isMicrojobAdmin && (
                                <MemberRowActions
                                  activeKey={rateBusy}
                                  actions={[
                                    {
                                      key: `edit:${row.jobType}`,
                                      label: 'Edit',
                                      onClick: () => openRateDialog(row),
                                    },
                                    {
                                      key: `remove:${row.jobType}`,
                                      label: 'Remove',
                                      onClick: () => handleRemoveRate(row),
                                    },
                                  ]}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      {!ratesLoading && visibleRates.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            align="center"
                            sx={{ py: 4, color: colors.textSecondary }}
                          >
                            No job type rates yet.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          )}
        </>
      )}

      {subTab === SUB_TAB.CYCLES && !hasMicrojobConfigured && (
        <VaultNotConfiguredBanner
          message="MicrojobPayment address is not configured. Save it in Settings before managing payout cycles."
          actionLabel="Go to Settings"
          onAction={() => setSubTab(SUB_TAB.SETTINGS)}
        />
      )}

      {subTab === SUB_TAB.CYCLES && hasMicrojobConfigured && (
        <>
          <Box sx={tabHeaderRowSx}>
            <Typography sx={{ color: colors.textPrimary, fontFamily: 'Questrial, sans-serif' }}>
              Payout cycles
            </Typography>
          </Box>

          {cyclesError && (
            <Alert severity="error" sx={tableAlertErrorSx}>
              {cyclesError}
            </Alert>
          )}

          <Box sx={{ ...filterToolbarSx, alignItems: 'flex-end' }}>
            <Box sx={{ ...fieldBlockStyles, minWidth: 160 }}>
              <Select
                value={cycleStatusFilter}
                onChange={(e) => setCycleStatusFilter(e.target.value)}
                displayEmpty
                fullWidth
                renderValue={(v) => {
                  if (!v) return 'All statuses';
                  const opt = CYCLE_STATUS_OPTIONS.find((o) => o.value === v);
                  return opt?.label || v;
                }}
                sx={makeSelectSx(false, !!cycleStatusFilter)}
                MenuProps={selectMenuProps}
              >
                {CYCLE_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value || 'all'} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            {isMicrojobOperator && (
              <Button
                variant="contained"
                onClick={openNewCycleDialog}
                sx={{ ...buttonStyles, ...memberHeaderActionBtnSx, ml: 'auto' }}
              >
                + New cycle
              </Button>
            )}
          </Box>

          <Box sx={tableCardStyles}>
            <TableContainer component={Box} sx={tableContainerStyles}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Cycle ID</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Total jobs</TableCell>
                    <TableCell>Total USDT</TableCell>
                    <TableCell>Contributors</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cyclesLoading && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={22} sx={{ color: colors.accent }} />
                      </TableCell>
                    </TableRow>
                  )}
                  {!cyclesLoading &&
                    pagedCycles.map((cycle) => (
                      <TableRow
                        key={cycle.cycleId}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => openCycleFromTable(cycle)}
                      >
                        <TableCell sx={walletCellSx}>{cycle.cycleId}</TableCell>
                        <TableCell>
                          {formatDateOnly(cycle.periodStart)} → {formatDateOnly(cycle.periodEnd)}
                        </TableCell>
                        <TableCell>
                          <MicrojobStatusBadge status={cycle.status} metaMap={CYCLE_STATUS_META} />
                        </TableCell>
                        <TableCell>{cycle.totalJobs ?? 0}</TableCell>
                        <TableCell>{cycle.totalPaymentUSDT ?? '0'}</TableCell>
                        <TableCell>{cycle.contributorCount ?? 0}</TableCell>
                        <TableCell align="right">
                          <MemberRowActions
                            activeKey={actionBusy}
                            actions={[
                              {
                                key: `open:${cycle.cycleId}`,
                                label: 'Open',
                                onClick: () => openCycleFromTable(cycle),
                              },
                              ...(isMicrojobOperator &&
                              (cycle.status === 'draft' || cycle.status === 'rejected')
                                ? [
                                    {
                                      key: `delete:${cycle.cycleId}`,
                                      label: 'Delete',
                                      onClick: () => handleDeleteCycle(cycle),
                                    },
                                  ]
                                : []),
                              ...(isMicrojobOperator && cycle.status === 'approved'
                                ? [
                                    {
                                      key: `execute:${cycle.cycleId}`,
                                      label: 'Execute on-chain',
                                      onClick: () =>
                                        executeCycleOnChain({
                                          cycleId: cycle.cycleId,
                                          busyKey: `execute:${cycle.cycleId}`,
                                        }),
                                    },
                                  ]
                                : []),
                            ]}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  {!cyclesLoading && filteredCycles.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: colors.textSecondary }}>
                        No cycles yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <PaymentsPagination
              pagination={cyclePagination}
              paginationItems={cyclePaginationItems}
              paginationLabel={cyclePaginationLabel}
              onPageChange={setCyclePage}
            />
          </Box>
        </>
      )}

      {/* Add / edit rate dialog */}
      <Dialog
        open={rateDialogOpen}
        onClose={() => !rateBusy && setRateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: memberDialogPaperSx }}
      >
        <DialogTitle sx={memberDialogTitleWrapSx}>
          <Typography component="span" sx={memberDialogTitleSx}>
            {editingExistingRate ? 'Edit job type' : 'Add job type'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={memberDialogContentSx}>
          <Box sx={memberFieldsStackSx}>
            <Box sx={fieldBlockStyles}>
              <Typography sx={memberFieldLabelSx}>
                Job type{' '}
                <Box component="span" sx={requiredSpanSx}>
                  *
                </Box>
              </Typography>
              <TextField
                fullWidth
                value={rateForm.jobType}
                onChange={(e) => {
                  const jobType = e.target.value;
                  setRateForm((f) => ({ ...f, jobType }));
                  setRateFormErrors((prev) => ({ ...prev, jobType: undefined }));
                }}
                disabled={!!rateBusy || editingExistingRate}
                placeholder="e.g. annotation"
                error={!!rateFormErrors.jobType}
                sx={formFieldStyles}
              />
              {rateFormErrors.jobType && (
                <Typography sx={fieldErrorTextSx}>{rateFormErrors.jobType}</Typography>
              )}
            </Box>
            <Box sx={fieldBlockStyles}>
              <Typography sx={memberFieldLabelSx}>
                Rate (USDT){' '}
                <Box component="span" sx={requiredSpanSx}>
                  *
                </Box>
              </Typography>
              <TextField
                fullWidth
                value={rateForm.rate}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
                  const [, decimals = ''] = raw.split('.');
                  if (decimals.length > 6) return;
                  setRateForm((f) => ({ ...f, rate: raw }));
                  setRateFormErrors((prev) => ({ ...prev, rate: undefined }));
                }}
                disabled={!!rateBusy}
                placeholder="40"
                inputMode="decimal"
                error={!!rateFormErrors.rate}
                sx={formFieldStyles}
              />
              {rateFormErrors.rate && (
                <Typography sx={fieldErrorTextSx}>{rateFormErrors.rate}</Typography>
              )}
            </Box>
          </Box>
          <Box sx={memberDialogBtnRowSx}>
            <Button disabled={!!rateBusy} onClick={() => setRateDialogOpen(false)} sx={memberCancelBtnSx}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!!rateBusy}
              onClick={handleSaveRate}
              sx={{ ...memberConfirmBtnSx, minWidth: 96, px: 2.5 }}
            >
              {rateBusy === 'save' ? (
                <CircularProgress size={20} sx={{ color: '#1a1d23' }} />
              ) : (
                'Save'
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Microjob cycle editor (period + job logs + preview) */}
      <Dialog
        open={cycleDialogOpen}
        onClose={closeCycleEditor}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: cycleModalPaperSx }}
      >
        <DialogTitle sx={memberDialogTitleWrapSx}>
          <Typography component="span" sx={memberDialogTitleSx}>
            Microjob cycle
          </Typography>
        </DialogTitle>
        <DialogContent sx={memberDialogContentSx}>
          {cycleBusy === 'load' ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} sx={{ color: colors.accent }} />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1,
                  mb: 2,
                }}
              >
                <Typography sx={{ ...cycleSectionLabelSx, mb: 0 }}>Cycle period</Typography>
                {cyclePreview?.status && (
                  <MicrojobStatusBadge status={cyclePreview.status} metaMap={CYCLE_STATUS_META} />
                )}
              </Box>
              <Box sx={{ ...formGridSx, mb: periodOverlapError || periodRangeInvalid ? 1 : 3 }}>
                <Box sx={fieldBlockStyles}>
                  <Typography sx={memberFieldLabelSx}>
                    Period start{' '}
                    <Box component="span" sx={requiredSpanSx}>
                      *
                    </Box>
                  </Typography>
                  <TextField
                    fullWidth
                    type="date"
                    value={cycleForm.periodStart}
                    onChange={(e) => {
                      const periodStart = e.target.value;
                      setCycleForm((f) => ({ ...f, periodStart }));
                    }}
                    disabled={cycleBusyAny}
                    error={Boolean(periodOverlapError || periodRangeInvalid)}
                    sx={dateFormFieldStyles}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
                <Box sx={fieldBlockStyles}>
                  <Typography sx={memberFieldLabelSx}>
                    Period end{' '}
                    <Box component="span" sx={requiredSpanSx}>
                      *
                    </Box>
                  </Typography>
                  <TextField
                    fullWidth
                    type="date"
                    value={cycleForm.periodEnd}
                    onChange={(e) => {
                      const periodEnd = e.target.value;
                      setCycleForm((f) => ({ ...f, periodEnd }));
                    }}
                    disabled={cycleBusyAny}
                    error={Boolean(periodOverlapError || periodRangeInvalid)}
                    sx={dateFormFieldStyles}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </Box>
              {(periodRangeInvalid || periodOverlapError) && (
                <Typography sx={{ ...fieldErrorTextSx, mb: 3, fontSize: '0.85rem' }}>
                  {periodRangeInvalid
                    ? 'Period end must be on or after period start.'
                    : periodOverlapError}
                </Typography>
              )}

              <Typography sx={cycleSectionLabelSx}>Job logs</Typography>
              {cyclePreview?.status === 'rejected' && (
                <Box sx={cycleRejectBoxSx}>
                  {cyclePreview.reviewNote
                    ? `Rejected: ${cyclePreview.reviewNote}`
                    : 'This cycle was rejected. Fix the job log if needed, then submit for review again.'}
                </Box>
              )}
              {!cycleEditorEntries.length && (
                <Box sx={cycleInfoBoxSx}>
                  No job logs imported yet. Import CSV to generate payout preview.
                </Box>
              )}
              {((cycleImportSummary?.invalidCount > 0) ||
                cycleEditorEntries.some((e) => e.validationStatus === 'invalid')) && (
                <Box sx={cycleWarningBoxSx}>
                  {cycleImportSummary?.invalidCount ||
                    cycleEditorEntries.filter((e) => e.validationStatus === 'invalid').length}{' '}
                  invalid row(s). See Reason in the table below.
                </Box>
              )}

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
                <Button
                  variant="outlined"
                  component="label"
                  disabled={cycleBusyAny || !canImportCycleCsv}
                  sx={cycleImportBtnSx}
                >
                  {cycleBusy === 'import' ? (
                    <CircularProgress size={18} sx={{ color: colors.accent }} />
                  ) : (
                    'Import CSV'
                  )}
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (file) handleCycleImportCsv(file);
                    }}
                  />
                </Button>
                <Button
                  variant="outlined"
                  disabled={cycleBusyAny}
                  onClick={downloadCsvTemplate}
                  sx={cycleSecondaryBtnSx}
                >
                  Download template
                </Button>
                {isSessionLogDao && (
                  <Button
                    variant="outlined"
                    disabled={cycleBusyAny || !canImportCycleCsv}
                    onClick={handleImportSessionLog}
                    sx={cycleImportBtnSx}
                  >
                    {cycleBusy === 'import-session' ? (
                      <CircularProgress size={18} sx={{ color: colors.accent }} />
                    ) : (
                      'Import from session log'
                    )}
                  </Button>
                )}
              </Box>

              {!!cycleEditorEntries.length && (
                <>
                  <Box sx={{ ...modalInnerTableCardSx, mb: 2 }}>
                    <TableContainer sx={modalInnerTableContainerSx}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Wallet</TableCell>
                            <TableCell>Job type</TableCell>
                            <TableCell>Job count</TableCell>
                            <TableCell>Line (USDT eq.)</TableCell>
                            <TableCell>Validation</TableCell>
                            <TableCell>Reason</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cycleEditorEntries.map((entry, idx) => (
                            <TableRow key={`${entry.contributorWallet}-${entry.jobType}-${idx}`}>
                              <TableCell sx={walletCellSx}>
                                {entry.contributorWallet}
                              </TableCell>
                              <TableCell>{entry.jobType}</TableCell>
                              <TableCell>{entry.jobCount}</TableCell>
                              <TableCell>{entry.lineAmountUsdt}</TableCell>
                              <TableCell>
                                <MicrojobStatusBadge
                                  status={entry.validationStatus}
                                  metaMap={ENTRY_STATUS_META}
                                />
                              </TableCell>
                              <TableCell
                                sx={{
                                  color:
                                    entry.validationStatus === 'invalid'
                                      ? '#dc2626'
                                      : colors.textSecondary,
                                  fontSize: '0.875rem',
                                  maxWidth: 220,
                                  whiteSpace: 'normal',
                                  lineHeight: 1.35,
                                }}
                              >
                                {entry.validationStatus === 'invalid'
                                  ? entry.validationError || '—'
                                  : '—'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  <Box sx={previewSummaryGridSx}>
                    <Box sx={previewSummaryCardSx}>
                      <Typography sx={previewSummaryLabelSx}>Contributors</Typography>
                      <Typography sx={previewSummaryValueSx}>
                        {cyclePreview?.contributorCount ?? cycleEditorPayouts.length ?? 0}
                      </Typography>
                    </Box>
                    <Box sx={previewSummaryCardSx}>
                      <Typography sx={previewSummaryLabelSx}>Total jobs</Typography>
                      <Typography sx={previewSummaryValueSx}>
                        {cyclePreview?.totalJobs ?? 0}
                      </Typography>
                    </Box>
                    <Box sx={previewSummaryCardSx}>
                      <Typography sx={previewSummaryLabelSx}>Total (USDT eq.)</Typography>
                      <Typography sx={previewSummaryValueSx}>
                        {cyclePreview?.totalPaymentUSDT ?? '0'}
                      </Typography>
                    </Box>
                    <Box sx={previewSummaryCardSx}>
                      <Typography sx={previewSummaryLabelSx}>Job rates snapshot</Typography>
                      <Typography sx={{ ...previewSummaryValueSx, fontSize: '0.95rem' }}>
                        {formatRatesSnapshot(cyclePreview?.ratesSnapshot)}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography sx={{ ...cycleSectionLabelSx, mt: 1 }}>
                    Contributor payout preview
                  </Typography>
                  <Box sx={{ ...modalInnerTableCardSx, mb: 1 }}>
                    <TableContainer sx={modalInnerTableContainerSx}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Wallet</TableCell>
                            <TableCell>Total jobs</TableCell>
                            <TableCell>Payout (USDT)</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cycleEditorPayouts.map((payout) => (
                            <TableRow key={payout.contributorWallet}>
                              <TableCell sx={walletCellSx}>
                                {payout.contributorWallet}
                              </TableCell>
                              <TableCell>{payout.totalJobs}</TableCell>
                              <TableCell>{payout.totalUsdtEquivalent}</TableCell>
                            </TableRow>
                          ))}
                          {!cycleEditorPayouts.length && (
                            <TableRow>
                              <TableCell
                                colSpan={3}
                                align="center"
                                sx={{ py: 3, color: colors.textSecondary }}
                              >
                                No contributor payouts yet.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </>
              )}

              <Box sx={memberDialogBtnRowSx}>
                <Button disabled={cycleBusyAny} onClick={closeCycleEditor} sx={memberCancelBtnSx}>
                  Cancel
                </Button>
                {isMicrojobOperator &&
                  cycleDraftId &&
                  (!cyclePreview?.status ||
                    cyclePreview.status === 'draft' ||
                    cyclePreview.status === 'rejected') && (
                    <Button
                      variant="contained"
                      disabled={cycleBusyAny || !!actionBusy}
                      onClick={() =>
                        handleDeleteCycle({
                          cycleId: cycleDraftId,
                          status: cyclePreview?.status || 'draft',
                        })
                      }
                      sx={memberDangerBtnSx}
                    >
                      {actionBusy === `delete:${cycleDraftId}` ? (
                        <CircularProgress size={20} sx={{ color: '#ffffff' }} />
                      ) : (
                        'Delete'
                      )}
                    </Button>
                  )}
                <Button
                  variant="contained"
                  disabled={cycleBusyAny || !canSubmitCycleEditor}
                  onClick={handleCycleSubmitForReview}
                  sx={memberConfirmBtnSx}
                >
                  {cycleBusy === 'submit' ? (
                    <CircularProgress size={20} sx={{ color: '#1a1d23' }} />
                  ) : (
                    'Submit for review'
                  )}
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Cycle review / execute dialog */}
      <Dialog
        open={!!selectedCycleId}
        onClose={() => {
          if (actionBusy) return;
          setSelectedCycleId(null);
          setCycleDetail(null);
          setDetailError('');
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: cycleModalPaperSx }}
      >
        <DialogTitle sx={memberDialogTitleWrapSx}>
          <Typography component="span" sx={memberDialogTitleSx}>
            Microjob cycle
          </Typography>
        </DialogTitle>
        <DialogContent sx={memberDialogContentSx}>
          {detailLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} sx={{ color: colors.accent }} />
            </Box>
          )}
          {detailError && (
            <Alert severity="error" sx={alertErrorSx}>
              {detailError}
            </Alert>
          )}
          {!detailLoading && cycleDetail && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1,
                  mb: 2,
                }}
              >
                <Typography sx={{ ...cycleSectionLabelSx, mb: 0 }}>Cycle period</Typography>
                <MicrojobStatusBadge status={cycleDetail.status} metaMap={CYCLE_STATUS_META} />
              </Box>
              <Box sx={{ ...formGridSx, mb: 3 }}>
                <Box sx={fieldBlockStyles}>
                  <Typography sx={memberFieldLabelSx}>Period start</Typography>
                  <TextField
                    fullWidth
                    type="date"
                    value={toDateInputValue(cycleDetail.periodStart)}
                    disabled
                    sx={{ ...dateFormFieldStyles, ...disabledFieldSx }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
                <Box sx={fieldBlockStyles}>
                  <Typography sx={memberFieldLabelSx}>Period end</Typography>
                  <TextField
                    fullWidth
                    type="date"
                    value={toDateInputValue(cycleDetail.periodEnd)}
                    disabled
                    sx={{ ...dateFormFieldStyles, ...disabledFieldSx }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </Box>

              <Typography sx={cycleSectionLabelSx}>Job logs</Typography>
              {canReview && (
                <Box sx={cycleInfoBoxSx}>Review, then approve or reject.</Box>
              )}
              {canExecute && (
                <Box sx={cycleInfoBoxSx}>Ready to execute on-chain.</Box>
              )}
              {cycleDetail.reviewNote && cycleDetail.status === 'rejected' && (
                <Box sx={cycleRejectBoxSx}>Rejected: {cycleDetail.reviewNote}</Box>
              )}
              {(cycleDetail.csvImportMeta?.invalidCount > 0 ||
                entries.some((e) => e.validationStatus === 'invalid')) && (
                <Box sx={cycleWarningBoxSx}>
                  {cycleDetail.csvImportMeta?.invalidCount ||
                    entries.filter((e) => e.validationStatus === 'invalid').length}{' '}
                  invalid row(s). See Reason in the table below.
                </Box>
              )}

              <Box sx={{ ...modalInnerTableCardSx, mb: 2 }}>
                <TableContainer sx={modalInnerTableContainerSx}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Wallet</TableCell>
                        <TableCell>Job type</TableCell>
                        <TableCell>Job count</TableCell>
                        <TableCell>Line (USDT eq.)</TableCell>
                        <TableCell>Validation</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {entries.map((entry, idx) => (
                        <TableRow key={`${entry.contributorWallet}-${entry.jobType}-${idx}`}>
                          <TableCell sx={walletCellSx}>{entry.contributorWallet}</TableCell>
                          <TableCell>{entry.jobType}</TableCell>
                          <TableCell>{entry.jobCount}</TableCell>
                          <TableCell>{entry.lineAmountUsdt}</TableCell>
                          <TableCell>
                            <MicrojobStatusBadge
                              status={entry.validationStatus}
                              metaMap={ENTRY_STATUS_META}
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              color:
                                entry.validationStatus === 'invalid'
                                  ? '#dc2626'
                                  : colors.textSecondary,
                              fontSize: '0.875rem',
                              maxWidth: 220,
                              whiteSpace: 'normal',
                              lineHeight: 1.35,
                            }}
                          >
                            {entry.validationStatus === 'invalid'
                              ? entry.validationError || '—'
                              : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                      {!entries.length && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 3, color: colors.textSecondary }}>
                            No job log entries.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box sx={previewSummaryGridSx}>
                <Box sx={previewSummaryCardSx}>
                  <Typography sx={previewSummaryLabelSx}>Contributors</Typography>
                  <Typography sx={previewSummaryValueSx}>
                    {cycleDetail.contributorCount ?? payouts.length ?? 0}
                  </Typography>
                </Box>
                <Box sx={previewSummaryCardSx}>
                  <Typography sx={previewSummaryLabelSx}>Total jobs</Typography>
                  <Typography sx={previewSummaryValueSx}>{cycleDetail.totalJobs ?? 0}</Typography>
                </Box>
                <Box sx={previewSummaryCardSx}>
                  <Typography sx={previewSummaryLabelSx}>Total (USDT eq.)</Typography>
                  <Typography sx={previewSummaryValueSx}>
                    {cycleDetail.totalPaymentUSDT ?? '0'}
                  </Typography>
                </Box>
                <Box sx={previewSummaryCardSx}>
                  <Typography sx={previewSummaryLabelSx}>Job rates snapshot</Typography>
                  <Typography sx={{ ...previewSummaryValueSx, fontSize: '0.95rem' }}>
                    {formatRatesSnapshot(cycleDetail.ratesSnapshot)}
                  </Typography>
                </Box>
              </Box>

              <Typography sx={{ ...cycleSectionLabelSx, mt: 1 }}>
                Contributor payout preview
              </Typography>
              <Box sx={{ ...modalInnerTableCardSx, mb: 1 }}>
                <TableContainer sx={modalInnerTableContainerSx}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Wallet</TableCell>
                        <TableCell>Total jobs</TableCell>
                        <TableCell>Payout (USDT)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payouts.map((payout) => (
                        <TableRow key={payout.contributorWallet}>
                          <TableCell sx={walletCellSx}>{payout.contributorWallet}</TableCell>
                          <TableCell>{payout.totalJobs}</TableCell>
                          <TableCell>{payout.totalUsdtEquivalent}</TableCell>
                        </TableRow>
                      ))}
                      {!payouts.length && (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ py: 3, color: colors.textSecondary }}>
                            No contributor payouts yet.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box sx={memberDialogBtnRowSx}>
                <Button
                  disabled={!!actionBusy}
                  onClick={() => {
                    if (actionBusy) return;
                    setSelectedCycleId(null);
                    setCycleDetail(null);
                    setDetailError('');
                  }}
                  sx={memberCancelBtnSx}
                >
                  Cancel
                </Button>
                {canReview && (
                  <>
                    <Button
                      variant="contained"
                      disabled={!!actionBusy}
                      onClick={() => setRejectOpen(true)}
                      sx={memberDangerBtnSx}
                    >
                      Reject
                    </Button>
                    <Button
                      variant="contained"
                      disabled={!!actionBusy}
                      onClick={handleApprove}
                      sx={memberConfirmBtnSx}
                    >
                      {actionBusy === 'approve' ? (
                        <CircularProgress size={20} sx={{ color: '#1a1d23' }} />
                      ) : (
                        'Approve'
                      )}
                    </Button>
                  </>
                )}
                {canExecute && (
                  <Button
                    variant="contained"
                    disabled={!!actionBusy}
                    onClick={handleExecute}
                    sx={memberConfirmBtnSx}
                  >
                    {actionBusy === 'execute' ? (
                      <CircularProgress size={20} sx={{ color: '#1a1d23' }} />
                    ) : (
                      'Execute on-chain'
                    )}
                  </Button>
                )}
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject note dialog */}
      <Dialog
        open={rejectOpen}
        onClose={() => !actionBusy && setRejectOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: memberDialogPaperSx }}
      >
        <DialogTitle sx={memberDialogTitleWrapSx}>
          <Typography component="span" sx={memberDialogTitleSx}>
            Reject cycle
          </Typography>
        </DialogTitle>
        <DialogContent sx={memberDialogContentSx}>
          <Box sx={fieldBlockStyles}>
            <Typography sx={memberFieldLabelSx}>Note (optional)</Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              disabled={!!actionBusy}
              sx={formFieldStyles}
            />
          </Box>
          <Box sx={memberDialogBtnRowSx}>
            <Button disabled={!!actionBusy} onClick={() => setRejectOpen(false)} sx={memberCancelBtnSx}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!!actionBusy}
              onClick={handleReject}
              sx={memberDangerBtnSx}
            >
              {actionBusy === 'reject' ? (
                <CircularProgress size={20} sx={{ color: '#ffffff' }} />
              ) : (
                'Reject'
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MicrojobSection;
